// Plantillas de Shorts Studio: cada una re-estiliza el video del usuario con Gemini Omni.
// El `prompt` es la instrucción que se manda al backend (video_shorts).
//
// Hay DOS familias (pedido de Imanol, viendo Higgsfield):
//   • 'entorno'        → la PERSONA queda IGUAL (realista, misma cara/ropa/movimientos); solo
//                        se cambia el FONDO y se agregan gráficos/texto alrededor (Bold Urban,
//                        Green Contrast, etc.). Sus prompts insisten en NO tocar a la persona.
//   • 'transformacion' → se re-estiliza TODO, la persona incluida (claymation, cómic…).
//
// ⚠️ Prompts DRAFT — se afinan testeando con Omni (sobre todo los 'entorno': hay que verificar
// que Omni respete el "mantené a la persona igual"). Previews (`gradient`) son placeholders.

export type ShortsTemplateTipo = 'entorno' | 'transformacion';

export interface ShortsTemplate {
  id: string;
  nombre: string;
  descripcion: string;
  tipo: ShortsTemplateTipo;
  /**
   * Motor de generación:
   *  • 'omni'    → transformación / entorno viejo (Omni directo).
   *  • 'compose' → entorno por composición (Omni-fondo + persona real recortada + subtítulos).
   *  • 'scene'   → entorno "scene" (Omni cambia el fondo + sync-3 corrige la boca + Topaz 4k).
   */
  engine?: 'omni' | 'compose' | 'scene';
  /** Instrucción para Omni (en inglés). Vacío en plantillas 'compose' (el backend define el estilo). */
  prompt: string;
  /** Preview placeholder: gradiente representativo del estilo (Tailwind). */
  gradient: string;
  /** Preview real: frame de un video de ejemplo (public/images/shorts/*.jpg). Si falta, cae al gradiente. */
  image?: string;
}

// ── Candado de la familia 'scene' ────────────────────────────────────────────
// En 'scene' la persona real NO se toca: Omni la re-dibuja si lo dejás (cambia pelo/cara y mete
// parpadeos/movimientos que no hiciste). sync-3 corrige la BOCA, pero que la cara/pelo/expresión no
// cambien depende de ESTE candado en el prompt. Se antepone a cada plantilla scene para que todas
// tengan el mismo blindaje fuerte (validado con la plantilla Radio). `scenePrompt(entorno)` arma el
// prompt final: [candado] + into <entorno> + [cierre].
const SCENE_LOCK =
  'Keep the person EXACTLY as filmed — 100% photorealistic and completely unchanged: the SAME face, ' +
  'hair, skin, eyes, body and clothes, and the SAME mouth and lip movements in sync with the audio. Do ' +
  'NOT redraw, stylize, beautify or alter the person in any way, and do NOT add blinks, head turns, ' +
  'expressions or any movement that is not already in the original footage. Change ONLY the environment ' +
  'behind and around them into';
const SCENE_TAIL =
  'Nothing (microphone, props or graphics) ever covers or crosses the face. Cinematic, photorealistic ' +
  'lighting that matches the scene. Vertical 9:16 talking-head short.';
function scenePrompt(entorno: string): string {
  return `${SCENE_LOCK} ${entorno} ${SCENE_TAIL}`;
}

export const SHORTS_TEMPLATES: ShortsTemplate[] = [
  // ── Modo "entorno" por COMPOSICIÓN (nuevo, engine 'compose'): Omni-fondo animado full-frame +
  //    persona real recortada + subtítulos sticker karaoke + emojis. La plantilla define el estilo. ──
  {
    id: 'pop',
    nombre: 'Pop!',
    descripcion: 'Vos igual · collage pop-art animado + subtítulos',
    tipo: 'entorno',
    engine: 'compose',
    prompt: '',
    gradient: 'from-pink-500 to-yellow-400',
    image: '/images/shorts/pop.jpg',
  },
  {
    id: 'titular',
    nombre: 'Titular',
    descripcion: 'Vos igual · recortes de diario animados + subtítulos',
    tipo: 'entorno',
    engine: 'compose',
    prompt: '',
    gradient: 'from-red-600 to-neutral-400',
    image: '/images/shorts/titular.jpg',
  },
  {
    // Claymation por COMPOSICIÓN-transform: Omni convierte TODO (vos + el lugar) a plastilina, no
    // se separa la persona. Reusa el análisis contextual (props de arcilla según el tema). Backend
    // template 'clay' (engine compose, modo transform).
    id: 'clay',
    nombre: 'Claymation',
    descripcion: 'Todo en arcilla · vos y el fondo en plastilina 3D',
    tipo: 'transformacion',
    engine: 'compose',
    prompt: '',
    gradient: 'from-orange-400 to-amber-700',
    image: '/images/shorts/clay.jpg',
  },
  // ── Familia "entorno" (viejas, engine omni): la persona queda igual, cambia el fondo ──
  {
    id: 'bold-urban',
    nombre: 'Bold Urban',
    descripcion: 'Tu cara igual · fondo de estudio + gráficos',
    tipo: 'entorno',
    prompt:
      'Keep the person 100% unchanged and fully photorealistic: the EXACT same face, mouth, lips, teeth, body, clothes and hair, with the SAME mouth and lip movements as the original video (perfect lip-sync). Do NOT redraw, stylize, smooth or alter the person in any way. Change ONLY the background behind them and add bold caption text and graphic elements (arrows, simple shapes, knobs) AROUND them — never over the face or mouth.',
    gradient: 'from-orange-500 to-red-700',
  },
  {
    id: 'contraste',
    nombre: 'Contraste',
    descripcion: 'Tu cara igual · fondo de color + texto grande',
    tipo: 'entorno',
    prompt:
      'Keep the person 100% unchanged and fully photorealistic: EXACT same face, mouth, lips and body, with the SAME mouth and lip movements as the original (perfect lip-sync). Do NOT redraw or stylize the person. Change ONLY the background to a single vivid solid color and add big bold caption text and simple graphic shapes AROUND them (never over the face), like a modern viral talking-head short.',
    gradient: 'from-emerald-500 to-red-600',
  },
  {
    id: 'tech-desk',
    nombre: 'Escritorio Tech',
    descripcion: 'Tu cara igual · escena tech + captions',
    tipo: 'entorno',
    prompt:
      'Keep the person 100% unchanged and fully photorealistic: EXACT same face, mouth, lips and body, with the SAME mouth and lip movements as the original (perfect lip-sync). Do NOT alter or stylize the person. Replace ONLY the background with a clean tech scene (desk, laptop, floating app windows and science doodles) and add large caption text AROUND them (never over the face).',
    gradient: 'from-sky-500 to-indigo-700',
  },
  {
    id: 'warm-doodles',
    nombre: 'Doodles Cálido',
    descripcion: 'Tu cara igual · garabatos + texto',
    tipo: 'entorno',
    prompt:
      'Keep the person 100% unchanged and fully photorealistic: EXACT same face, mouth, lips and body, with the SAME mouth and lip movements as the original (perfect lip-sync). Do NOT modify or stylize the person. Change ONLY the background to a warm-toned scene decorated with hand-drawn yellow doodles, arrows and large caption text AROUND them (never over the face).',
    gradient: 'from-amber-400 to-orange-600',
  },

  // ── Familia "transformación": se re-estiliza todo, la persona incluida ──
  {
    id: 'comic',
    nombre: 'Comic',
    descripcion: 'Transformación total · cómic pop-art',
    tipo: 'transformacion',
    prompt:
      'Transform this video into a bold comic-book and pop-art style: heavy black ink outlines, halftone dot shading and a vivid, saturated palette.',
    gradient: 'from-blue-500 to-fuchsia-700',
  },

  // ── Familia 'scene' (pipeline NUEVO validado): Omni cambia SOLO el entorno (rompe el lip-sync) →
  //    sync-3 corrige la boca contra el audio real → Topaz 4k → downscale 1080×1920. La persona real
  //    queda intacta (candado `scenePrompt`). Cada plantilla = solo su entorno; el resto es el motor. ──
  {
    id: 'radio',
    nombre: 'Radio',
    descripcion: 'Vos igual · estudio de radio + ON AIR',
    tipo: 'entorno',
    engine: 'scene',
    prompt: scenePrompt(
      'a professional radio broadcast studio: a glowing red neon "ON AIR" sign on the wall, warm moody ' +
        'studio lighting, dark acoustic foam panels, a softly blurred mixing console, and a large studio ' +
        'microphone on a boom arm coming in from the side',
    ),
    gradient: 'from-red-700 to-neutral-900',
  },
  {
    id: 'podcast',
    nombre: 'Podcast',
    descripcion: 'Vos igual · set de podcast + mic de brazo',
    tipo: 'entorno',
    engine: 'scene',
    prompt: scenePrompt(
      'a modern podcast studio: a large boom-arm condenser microphone in the foreground, headphones, ' +
        'sound-treated acoustic panels, warm LED and soft RGB accent lighting, a few plants and a cozy ' +
        'creator-studio vibe',
    ),
    gradient: 'from-fuchsia-600 to-purple-800',
  },
  // NOTA: "Noticiero" se sacó — Gemini Omni BLOQUEA de forma consistente poner a una persona real
  // en un set de noticias (política anti-deepfake de noticieros). Validado en el lab: el bloqueo es
  // por el entorno "news/anchor/broadcast", NO por el candado ni por el video. Ver ADR-2026-08-18.
  {
    id: 'jet-privado',
    nombre: 'Jet Privado',
    descripcion: 'Vos igual · interior de jet de lujo',
    tipo: 'entorno',
    engine: 'scene',
    prompt: scenePrompt(
      'the luxurious interior of a private jet: cream leather seats, polished wood trim, oval windows ' +
        'showing a bright sky with soft clouds, warm ambient lighting and an exclusive first-class vibe',
    ),
    gradient: 'from-amber-300 to-yellow-700',
  },
  {
    id: 'penthouse',
    nombre: 'Penthouse',
    descripcion: 'Vos igual · penthouse con skyline de noche',
    tipo: 'entorno',
    engine: 'scene',
    prompt: scenePrompt(
      'a luxury penthouse at night: floor-to-ceiling windows with a glowing city skyline behind, sleek ' +
        'designer furniture, warm ambient lighting and an aspirational high-end vibe',
    ),
    gradient: 'from-indigo-900 to-slate-800',
  },
  {
    id: 'neon-tokyo',
    nombre: 'Neon Tokyo',
    descripcion: 'Vos igual · calle de Tokio con neones',
    tipo: 'entorno',
    engine: 'scene',
    prompt: scenePrompt(
      'a Tokyo street at night in the rain: glowing Japanese neon signs in pink, red and cyan, wet ' +
        'reflective pavement, colorful bokeh city lights and a cinematic cyberpunk vibe',
    ),
    gradient: 'from-fuchsia-600 to-cyan-500',
  },
];

export function getShortsTemplate(id: string | null | undefined): ShortsTemplate | undefined {
  return SHORTS_TEMPLATES.find((t) => t.id === id);
}

/**
 * Rollout controlado del motor 'scene' (Omni + sync-3 + Topaz 4k): arranca APAGADO.
 * Mientras esté en false, las plantillas 'scene' (Radio/Podcast/Jet/Penthouse/Neon) solo las ven
 * los dev_testers — el resto de usuarios ve únicamente las de siempre (Pop!/Titular/Clay). Se abre
 * a todos borrando este freno (ADR-2026-10-22-fuera-los-interruptores-de-lo-lanzado: no se deja en true).
 */
export const SHORTS_SCENE_ENABLED = false;

/**
 * Plantillas visibles para un usuario. Oculta las 'scene' salvo que el motor esté habilitado
 * globalmente (SHORTS_SCENE_ENABLED) o el usuario sea dev_tester. `isDevTester === null` (todavía
 * cargando el grupo) se trata como NO dev_tester → no se filtran de más las de siempre.
 */
export function shortsTemplatesVisibles(isDevTester: boolean | null): ShortsTemplate[] {
  if (SHORTS_SCENE_ENABLED) return SHORTS_TEMPLATES;
  return SHORTS_TEMPLATES.filter((t) => t.engine !== 'scene' || isDevTester === true);
}
